/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';

import Avatar from '@atlaskit/avatar/avatar';
import { RovoHexIcon } from '@atlaskit/logo/rovo-hex/icon';
import {
	AGENT_AVATAR_CLIP_PATH,
	AgentAvatar,
} from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import { token } from '@atlaskit/tokens';

import type { SuggestionProfile } from '../../aiSuggestionsPluginType';

/**
 * A profile in the avatar stack. An agent slot with no `profile` means the suggestion had no
 * `createdByAgent` — renders the generic Rovo hex logo instead.
 */
export type SuggestionAvatarProfile =
	| { isAgent: true; profile?: SuggestionProfile }
	| { isAgent: false; profile: SuggestionProfile };

// Matches `Avatar`'s own `borderColor` ring (a 2px box-shadow) below, so both avatar types line
// up. `color.border.inverse` reads as white in light mode against the avatar's bold background,
// and flips to a dark ring in dark mode for the same contrast.
const avatarRingColor = token('color.border.inverse');

const styles = cssMap({
	// Sized to match `Avatar`'s/`AgentAvatar`'s own `xxsmall` footprint. Shared by both avatar
	// types so their manually-drawn rings (below) are drawn identically — same wrapper/content
	// sizing, only the ring's shape/clip differs.
	avatarRingWrapper: {
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: token('space.200'),
		width: token('space.200'),
	},
	// Hand-drawn hexagon ring for agents — `AgentAvatar`'s own `showBorder` inset is too thin at
	// `xxsmall`. `inset: -2px` centers this via exact pixel math off `avatarRingWrapper`'s own
	// box, avoiding the sub-pixel drift a transform-based centering approach could introduce.
	agentAvatarRing: {
		position: 'absolute',
		zIndex: 0,
		inset: '-2px',
		backgroundColor: avatarRingColor,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		clipPath: AGENT_AVATAR_CLIP_PATH,
	},
	// Hand-drawn circular ring for users — drawn manually (rather than via `Avatar`'s own
	// `borderColor`, which is suppressed with `borderColor="transparent"` at the call site) so
	// every avatar in the stack, agent or user, gets the exact same 2px white ring treatment.
	userAvatarRing: {
		position: 'absolute',
		zIndex: 0,
		inset: '-2px',
		backgroundColor: avatarRingColor,
		borderRadius: token('radius.full'),
	},
	avatarRingContent: {
		position: 'relative',
		zIndex: 1,
		// Explicit size + centering forces the content dead-center regardless of its own box
		// model. `fontSize: 0` strips any line-box height it could otherwise inherit, which
		// would offset it via baseline alignment instead of true centering.
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: token('space.200'),
		width: token('space.200'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography -- Not real text; collapses the phantom inline line-box, no typography token applies.
		fontSize: 0,
	},
});

/**
 * One avatar's ring + content, given its category (agent/user/generic Rovo fallback) — see
 * `SuggestionAvatarProfile`. Used by `SuggestionAvatarIndicator` to render each entry in the
 * stack.
 */
export function SuggestionAvatar({ isAgent, profile }: SuggestionAvatarProfile): JSX.Element {
	if (isAgent) {
		return (
			<span css={styles.avatarRingWrapper}>
				<span css={styles.agentAvatarRing} />
				<span css={styles.avatarRingContent}>
					{profile ? (
						<AgentAvatar
							imageUrl={profile.picture?.trim() || undefined}
							agentIdentityAccountId={profile.accountId}
							name={profile.name}
							label={profile.name}
							size="xxsmall"
							showBorder={false}
						/>
					) : (
						<RovoHexIcon size="xxsmall" />
					)}
				</span>
			</span>
		);
	}

	return (
		<span css={styles.avatarRingWrapper}>
			<span css={styles.userAvatarRing} />
			<span css={styles.avatarRingContent}>
				<Avatar
					name={profile.name}
					src={profile.picture ?? undefined}
					size="xxsmall"
					// Ring drawn manually via `userAvatarRing` above (consistent 2px white ring
					// across every avatar in the stack) — this suppresses `Avatar`'s own default
					// box-shadow ring.
					borderColor="transparent"
				/>
			</span>
		</span>
	);
}
